import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { ClientsDocument, OutreachQueueDocument } from "@/graphql/clients.generated";
import { queueDay } from "@/pages/Alumni/helpers/outreachQueue";

export interface AlumniNavCounts {
    /** Every alumnus in the directory. */
    directory: number;
    /** Every entry in the outreach queue, what its "All" filter counts. */
    queue: number;
}

/**
 * The counts on the alumni console's nav. Chat has none: each conversation in the inbox carries its
 * own unread badge.
 *
 * Both use the very queries their pages make, so every mutation that already refetches
 * `ClientsDocument` or `OutreachQueueDocument` (logging a contact, scheduling a follow-up, an
 * import) moves the badge with it. The directory's is the same request the directory page makes,
 * served from the cache there. The queue's asks for one entry: only its `total` is read.
 */
export const useAlumniNavCounts = (): AlumniNavCounts => {
    const today = useMemo(() => new Date(), []);
    const { data: clientsData } = useQuery(ClientsDocument);
    const { data: queueData } = useQuery(OutreachQueueDocument, { variables: { ...queueDay(today), limit: 1 } });

    return {
        directory: clientsData?.clients.length ?? 0,
        queue: queueData?.outreachQueue.total ?? 0,
    };
};
